import axios from 'axios'

/*
 * Notifications push sur cet appareil (Windows, Android, iPhone installé sur l'écran d'accueil).
 * Le service worker public/sw.js les affiche ; le serveur les envoie (App\Services\Notifications\WebPushSender).
 *
 * L'appareil est rattaché à l'utilisateur qui a activé les notifications : à la déconnexion il est
 * retiré côté serveur, puis rattaché de nouveau à la reconnexion du même utilisateur seulement
 * (sur un poste partagé, un autre utilisateur doit activer les notifications lui-même).
 */

// Version dans l'adresse : le CDN de l'hébergeur a pu garder en cache une ancienne réponse de /sw.js
const SW_URL = '/sw.js?v=1'
const OWNER_KEY = 'push_owner_id'

const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id ?? null
  } catch (e) {
    return null
  }
}

const readOwner = () => {
  try { return localStorage.getItem(OWNER_KEY) } catch (e) { return null }
}

const writeOwner = (id) => {
  try {
    if (id) localStorage.setItem(OWNER_KEY, String(id))
    else localStorage.removeItem(OWNER_KEY)
  } catch (e) { /* stockage indisponible : l'appareil sera simplement réactivé à la main */ }
}

export const isPushSupported = () =>
  typeof window !== 'undefined'
  && window.isSecureContext
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true

/** Sur iPhone, Safari n'autorise le push qu'à l'application ajoutée à l'écran d'accueil. */
export const needsHomeScreenInstall = () => isIos() && !isStandalone()

/** « Windows · Edge », « Android · Chrome »… pour reconnaître l'appareil dans la liste. */
export const deviceLabel = () => {
  const ua = navigator.userAgent
  const os = /windows/i.test(ua) ? 'Windows'
    : /android/i.test(ua) ? 'Android'
      : isIos() ? 'iPhone / iPad'
        : /mac os/i.test(ua) ? 'Mac'
          : /linux/i.test(ua) ? 'Linux' : 'Appareil'
  const browser = /edg\//i.test(ua) ? 'Edge'
    : /opr\//i.test(ua) ? 'Opera'
      : /firefox|fxios/i.test(ua) ? 'Firefox'
        : /chrome|crios/i.test(ua) ? 'Chrome'
          : /safari/i.test(ua) ? 'Safari' : 'Navigateur'
  return `${os} · ${browser}`
}

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, c => c.charCodeAt(0))
}

// Même empreinte que PushSubscription::hashEndpoint() côté serveur
const sha256 = async (text) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')
}

const registration = async () => {
  const existing = await navigator.serviceWorker.getRegistration('/')
  return existing || navigator.serviceWorker.register(SW_URL, { scope: '/' })
}

const currentSubscription = async () => {
  if (!isPushSupported()) return null
  const reg = await navigator.serviceWorker.getRegistration('/')
  return reg ? reg.pushManager.getSubscription() : null
}

const sendToServer = (subscription) => {
  const json = subscription.toJSON()
  return axios.post('/api/push-subscriptions', {
    endpoint: json.endpoint,
    keys: json.keys,
    content_encoding: (window.PushManager.supportedContentEncodings || []).includes('aes128gcm') ? 'aes128gcm' : 'aesgcm',
    device_label: deviceLabel()
  })
}

/**
 * État de cet appareil : pris en charge, autorisation du navigateur, activé pour l'utilisateur,
 * et réglage du serveur (clés VAPID présentes) avec la liste des appareils de l'utilisateur.
 */
export async function getPushState() {
  const supported = isPushSupported()
  const { data } = await axios.get('/api/push-subscriptions')
  const subscription = supported ? await currentSubscription() : null
  const devices = data.devices || []
  const hash = subscription ? await sha256(subscription.endpoint) : null
  const current = devices.find(d => d.endpoint_hash === hash) || null

  return {
    supported,
    serverEnabled: !!data.enabled,
    needsInstall: needsHomeScreenInstall(),
    permission: supported ? Notification.permission : 'unsupported',
    subscribed: !!current && readOwner() === String(currentUserId()),
    currentDeviceId: current?.id ?? null,
    devices,
    publicKey: data.public_key
  }
}

/** Demande l'autorisation au navigateur et abonne cet appareil (à appeler sur un clic). */
export async function enablePush() {
  const { data } = await axios.get('/api/push-subscriptions')
  if (!data.enabled || !data.public_key) {
    throw new Error("Les notifications push ne sont pas configurées sur le serveur.")
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Les notifications sont bloquées pour ce site dans le navigateur.'
      : "L'autorisation n'a pas été accordée.")
  }

  let reg
  try {
    reg = await registration()
  } catch (e) {
    // Ex. /sw.js renvoyé en HTML : le serveur ne sert pas le fichier (voir .htaccess à la racine)
    console.error('Service worker non installé :', e)
    throw new Error("Le service de notifications n'a pas pu être installé sur ce navigateur. Réessayez plus tard ou contactez l'administrateur.")
  }
  await navigator.serviceWorker.ready
  let subscription = await reg.pushManager.getSubscription()
  // Abonnement créé avec d'autres clés (serveur réinstallé) : il faut le recréer
  const key = urlBase64ToUint8Array(data.public_key)
  const currentKey = subscription?.options?.applicationServerKey
  if (subscription && currentKey && !new Uint8Array(currentKey).every((b, i) => b === key[i])) {
    await subscription.unsubscribe()
    subscription = null
  }
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  }

  await sendToServer(subscription)
  writeOwner(currentUserId())
}

/** Désactive les notifications push sur cet appareil. */
export async function disablePush() {
  const subscription = await currentSubscription()
  if (subscription) {
    await axios.delete('/api/push-subscriptions', { data: { endpoint: subscription.endpoint } }).catch(() => {})
    await subscription.unsubscribe().catch(() => {})
  }
  writeOwner(null)
}

/** Retire un autre appareil de l'utilisateur (liste des appareils). */
export const removePushDevice = (id) => axios.delete('/api/push-subscriptions', { data: { id } })

export const sendTestPush = async () => (await axios.post('/api/push-subscriptions/test')).data.sent || 0

/**
 * À l'ouverture de l'application : réenregistre l'abonnement de cet appareil (le navigateur peut
 * le renouveler) s'il appartient à l'utilisateur connecté.
 */
export async function syncPushSubscription() {
  try {
    if (!isPushSupported() || Notification.permission !== 'granted') return
    if (readOwner() !== String(currentUserId())) return
    const subscription = await currentSubscription()
    if (subscription) await sendToServer(subscription)
  } catch (e) {
    // Silencieux : l'utilisateur peut réactiver les notifications depuis ses préférences
  }
}

/** À la déconnexion : cet appareil ne reçoit plus les notifications de l'utilisateur. */
export async function forgetPushDevice() {
  try {
    const subscription = await currentSubscription()
    if (subscription) {
      await axios.delete('/api/push-subscriptions', { data: { endpoint: subscription.endpoint } })
    }
  } catch (e) {
    // Sans réseau : le serveur garde l'appareil jusqu'à la prochaine désactivation
  }
}
